import dayjs from 'dayjs';
import lib from '../assets/utils/lib';
import {
  DATA_SCOPE_PA,
  formatCurrencyNgn,
  formatMonthsLabel,
  isPaApprovedScope,
} from '../components/Organizations/orgPaymentAdviceHelpers';

export const PAYMENT_ADVICE_RECONCILE_STORAGE_KEY = 'finance_reconcile_payment_advice_params';

const PA_DASHBOARD_QUERY_KEYS = [
  'org_id',
  'date_from',
  'date_to',
  'date_type',
  'status',
  'location_id',
  'plan_type_id',
  'plan_ids',
  'checkin_type_ids',
];

export function getFieldGuideEntry(guide, field) {
  if (!Array.isArray(guide) || !field) return null;
  return guide.find((g) => g.field === field || g.key === field) || null;
}

/** End-user tooltip copy — prefer help_text; never fall back to description. */
export function getFieldGuideHelpText(guide, field) {
  return getFieldGuideEntry(guide, field)?.help_text || null;
}

export function getFieldGuideHowToUse(guide, field) {
  return getFieldGuideEntry(guide, field)?.how_to_use || null;
}

/**
 * End-user tooltip body for a metric: help_text, optionally with how_to_use.
 * Never includes developer `description`.
 */
export function buildFieldGuideTooltip(guide, field) {
  const helpText = getFieldGuideHelpText(guide, field);
  const howToUse = getFieldGuideHowToUse(guide, field);
  if (!helpText && !howToUse) return null;
  if (!howToUse) return helpText;
  if (!helpText) return howToUse;
  return `${helpText}\n\nHow to use: ${howToUse}`;
}

/** Developer / technical detail — do not show in end-user UI by default. */
export function getFieldGuideDescription(guide, field) {
  return getFieldGuideEntry(guide, field)?.description || null;
}

export function getFieldGuideLabel(guide, field, fallback) {
  return getFieldGuideEntry(guide, field)?.label || fallback;
}

export function isFieldGuidePrimary(guide, field) {
  return getFieldGuideEntry(guide, field)?.primary === true;
}

/** Section help from `help.sections[]` (card / table titles). */
export function getHelpSection(help, sectionId) {
  const sections = help?.sections;
  if (!Array.isArray(sections) || !sectionId) return null;
  return (
    sections.find((s) => s.id === sectionId || s.key === sectionId) || null
  );
}

export function getHelpSectionTitle(help, sectionId, fallback) {
  return getHelpSection(help, sectionId)?.title || fallback;
}

/** Tooltip for a dashboard section title — help_text + optional warning. */
export function getHelpSectionTooltip(help, sectionId) {
  const section = getHelpSection(help, sectionId);
  if (!section) return null;
  const parts = [section.help_text, section.warning].filter(Boolean);
  return parts.length ? parts.join('\n\n') : null;
}

export function mergeFinanceHelp(primary, fallback) {
  if (!primary && !fallback) return null;
  if (!primary) return fallback;
  if (!fallback) return primary;
  return {
    ...fallback,
    ...primary,
    why_numbers_differ: primary.why_numbers_differ?.length
      ? primary.why_numbers_differ
      : fallback.why_numbers_differ,
    tips: primary.tips?.length ? primary.tips : fallback.tips,
  };
}

export function getSchemaHelpForScope(schema, dataScope) {
  const byScope = schema?.help_by_scope;
  if (!byScope || !dataScope) return null;
  return byScope[dataScope] || null;
}

export function buildPaDashboardCompareUrl(params = {}) {
  const normalized = normalizePaDashboardCompareParams(params);
  const qs = new URLSearchParams();
  PA_DASHBOARD_QUERY_KEYS.forEach((key) => {
    const value = normalized[key];
    if (value != null && value !== '') {
      qs.set(key, String(value));
    }
  });
  const query = qs.toString();
  return `/dashboard/preauth${query ? `?${query}` : ''}`;
}

/**
 * Merge API reconciliation params with payment-advice context (org in focus, period, scope).
 */
export function normalizePaDashboardCompareParams(apiParams = {}, context = {}) {
  const shared =
    context.sharedFilters
    || apiParams.shared_filters
    || context.reconciliation?.shared_filters
    || {};
  const merged = { ...shared, ...(apiParams || {}) };

  const orgId =
    merged.org_id
    ?? merged.organization_id
    ?? context.organizationId
    ?? context.organization_id;

  if (orgId != null && orgId !== '') {
    merged.org_id = orgId;
  }

  if (!merged.date_from || !merged.date_to) {
    const fromPeriod = periodToPaDashboardDates(context.period);
    Object.assign(merged, fromPeriod);
  }

  if (!merged.status && context.dataScope === 'pa_approved') {
    merged.status = 'Approved';
  }

  if (merged.facility_id != null && merged.facility_id !== '' && !merged.location_id) {
    merged.location_id = merged.facility_id;
  }

  if (merged.encounter_status && !merged.status) {
    merged.status = merged.encounter_status;
  }

  return merged;
}

export function periodToPaDashboardDates(period) {
  if (!period?.type) return {};

  if (period.type === 'month_range' && period.month_from && period.month_to) {
    const start = dayjs(`${period.month_from}-01`);
    const end = dayjs(`${period.month_to}-01`).endOf('month');
    return {
      date_from: start.format('YYYY-MM-DD'),
      date_to: end.format('YYYY-MM-DD'),
      date_type: 'request_date',
    };
  }

  if (period.type === 'date_range' && period.date_from && period.date_to) {
    return {
      date_from: period.date_from,
      date_to: period.date_to,
      date_type: 'request_date',
    };
  }

  if (period.type === 'month' && period.month) {
    const start = dayjs(`${period.month}-01`);
    return {
      date_from: start.format('YYYY-MM-DD'),
      date_to: start.endOf('month').format('YYYY-MM-DD'),
      date_type: 'request_date',
    };
  }

  return {};
}

/**
 * Build payment-advice reconcile payload when navigating from PA dashboard.
 */
export function normalizePaymentAdviceReconcileParams(apiParams = {}, context = {}) {
  const shared = apiParams.shared_filters || context.sharedFilters || {};
  const merged = { ...shared, ...(apiParams || {}) };

  const orgId =
    merged.organization_id
    ?? merged.org_id
    ?? context.organizationId
    ?? context.orgId;

  if (orgId != null && orgId !== '') {
    merged.organization_id = orgId;
  }

  if (!merged.period && context.period) {
    merged.period = context.period;
  }

  if (!merged.report_options && context.reportOptions) {
    merged.report_options = context.reportOptions;
  }

  return merged;
}

export function paDashboardDatesToPaymentAdvicePeriod(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return null;
  const start = dayjs(dateFrom);
  const end = dayjs(dateTo);
  if (!start.isValid() || !end.isValid()) return null;

  if (start.date() === 1 && end.date() === end.daysInMonth()
    && start.format('YYYY-MM') === end.format('YYYY-MM')) {
    return { type: 'month', month: start.format('YYYY-MM') };
  }

  if (start.date() === 1 && end.date() === end.daysInMonth()
    && start.year() === end.year() && start.month() !== end.month()) {
    return {
      type: 'month_range',
      month_from: start.format('YYYY-MM'),
      month_to: end.format('YYYY-MM'),
    };
  }

  return {
    type: 'date_range',
    date_from: start.format('YYYY-MM-DD'),
    date_to: end.format('YYYY-MM-DD'),
  };
}

export function storePaymentAdviceReconcileParams(params) {
  if (params && typeof params === 'object') {
    sessionStorage.setItem(PAYMENT_ADVICE_RECONCILE_STORAGE_KEY, JSON.stringify(params));
  }
}

export function consumePaymentAdviceReconcileParams() {
  try {
    const raw = sessionStorage.getItem(PAYMENT_ADVICE_RECONCILE_STORAGE_KEY);
    sessionStorage.removeItem(PAYMENT_ADVICE_RECONCILE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function buildOrganizationRouteHash(org) {
  if (!org) return null;
  if (org.hash) return org.hash;
  if (org.org_social_no && typeof btoa === 'function') {
    try {
      return btoa(String(org.org_social_no));
    } catch {
      return null;
    }
  }
  return null;
}

function matchesOrganizationId(org, organizationId) {
  if (!org || organizationId == null || organizationId === '') return false;
  const target = String(organizationId);
  return [org.org_id, org.id, org.organization_id].some(
    (value) => value != null && String(value) === target,
  );
}

export async function resolveOrganizationById(organizationId) {
  if (organizationId == null || organizationId === '') return null;
  const idStr = String(organizationId);

  const tryList = async (fetcher) => {
    try {
      const list = await fetcher();
      if (!Array.isArray(list) || !list.length) return null;
      const exact = list.find((org) => matchesOrganizationId(org, organizationId));
      return exact || null;
    } catch {
      return null;
    }
  };

  const fromV2Mini = await tryList(async () => {
    const resp = await lib.getOrganizationsV2({ mode: 'mini', search: idStr, limit: 50, page: 1 });
    const parsed = lib.parseApiEnvelope(resp);
    return parsed.ok && Array.isArray(parsed.data) ? parsed.data : [];
  });
  if (fromV2Mini) return fromV2Mini;

  const fromV2Search = await tryList(async () => {
    const resp = await lib.getOrganizationsV2({ search: idStr, limit: 50, page: 1 });
    const parsed = lib.parseApiEnvelope(resp);
    return parsed.ok && Array.isArray(parsed.data) ? parsed.data : [];
  });
  if (fromV2Search) return fromV2Search;

  const fromLegacy = await tryList(async () => {
    const resp = await lib.orgs({ mode: 'mini', search: idStr });
    const body = resp?.data;
    if (body?.error || !Array.isArray(body?.data)) return [];
    return body.data;
  });
  if (fromLegacy) return fromLegacy;

  return null;
}

export async function resolveOrganizationPaymentAdviceUrl(organizationId) {
  const org = await resolveOrganizationById(organizationId);
  const hash = buildOrganizationRouteHash(org);
  if (!hash) return null;
  return `/corporations/users/${encodeURIComponent(hash)}?tab=payment_advice`;
}

export function getReconciliationBlock(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return payload.reconciliation || payload.scope?.reconciliation || null;
}

export function getFinanceHelpBlock(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return payload.help || null;
}

export function applyPaymentAdviceReconcileToForm(form, stored, defaults = {}) {
  if (!stored || !form) return false;
  const patch = {};

  if (stored.report_options) {
    patch.report_options = { ...defaults, ...stored.report_options };
  }

  const period = stored.period;
  if (period?.type === 'month') {
    patch.period_type = 'month';
    patch.single_month = dayjs(`${period.month}-01`);
  } else if (period?.type === 'month_range' && period.month_from && period.month_to) {
    patch.period_type = 'month_range';
    patch.month_range = [
      dayjs(`${period.month_from}-01`),
      dayjs(`${period.month_to}-01`),
    ];
  } else if (period?.type === 'date_range' && period.date_from && period.date_to) {
    patch.period_type = 'date_range';
    patch.date_range = [dayjs(period.date_from), dayjs(period.date_to)];
  }

  if (stored.report_variant) {
    patch.report_variant = stored.report_variant;
  }

  if (Object.keys(patch).length) {
    form.setFieldsValue(patch);
    return true;
  }
  return false;
}

export function getPaymentAdviceSummaryMetrics(preview) {
  if (!preview?.summary) return [];
  const summary = preview.summary;
  const fieldGuide = preview.field_guide || [];
  const isPa = isPaApprovedScope({
    data_scope: preview.scope?.data_scope || preview.report_options?.data_scope,
  });

  const metrics = [
    {
      field: 'total_enrollees',
      label: getFieldGuideLabel(fieldGuide, 'total_enrollees', 'Enrollees'),
      value: summary.total_enrollees ?? '—',
      helpText: getFieldGuideHelpText(fieldGuide, 'total_enrollees'),
    },
    {
      field: 'total_encounters',
      label: getFieldGuideLabel(fieldGuide, 'total_encounters', 'Encounters'),
      value: summary.total_encounters ?? '—',
      helpText: getFieldGuideHelpText(fieldGuide, 'total_encounters'),
    },
    {
      field: 'months_covered',
      label: 'Months',
      value: formatMonthsLabel(summary.months_covered),
      wide: true,
    },
  ];

  if (isPa) {
    metrics.push({
      field: 'total_pa_amount',
      label: getFieldGuideLabel(fieldGuide, 'total_pa_amount', 'Total PA amount'),
      value: formatCurrencyNgn(summary.total_pa_amount ?? summary.total_approved_pa),
      currency: true,
      primary: isFieldGuidePrimary(fieldGuide, 'total_pa_amount') || true,
      helpText: getFieldGuideHelpText(fieldGuide, 'total_pa_amount'),
    });
    if (summary.total_approved_cost != null) {
      metrics.push({
        field: 'total_approved_cost',
        label: getFieldGuideLabel(fieldGuide, 'total_approved_cost', 'Total approved cost'),
        value: formatCurrencyNgn(summary.total_approved_cost),
        currency: true,
        deprecated: true,
        helpText: getFieldGuideHelpText(fieldGuide, 'total_approved_cost'),
      });
    }
  } else {
    metrics.push({
      field: 'total_approved_cost',
      label: getFieldGuideLabel(fieldGuide, 'total_approved_cost', 'Claims approved'),
      value: formatCurrencyNgn(summary.total_approved_cost),
      currency: true,
      primary: isFieldGuidePrimary(fieldGuide, 'total_approved_cost') || true,
      helpText: getFieldGuideHelpText(fieldGuide, 'total_approved_cost'),
    });
    metrics.push({
      field: 'total_approved_pa',
      label: getFieldGuideLabel(fieldGuide, 'total_approved_pa', 'PA reference'),
      value: formatCurrencyNgn(summary.total_approved_pa),
      currency: true,
      helpText: getFieldGuideHelpText(fieldGuide, 'total_approved_pa'),
    });
  }

  return metrics;
}

export function getStatusLabelHelp(help, status) {
  if (!help?.status_labels || !status) return null;
  return help.status_labels[status] || null;
}

/**
 * Normalize PA analytics list endpoints (time-series / breakdown).
 * Supports legacy `data: []` and enriched `{ series|buckets|items|data, field_guide, help, totals }`.
 */
export function parsePaAnalyticsListPayload(body) {
  const root = body?.data;
  if (Array.isArray(root)) {
    return { rows: root, field_guide: [], help: null, totals: null };
  }
  if (!root || typeof root !== 'object') {
    return { rows: [], field_guide: [], help: null, totals: null };
  }
  const rows = Array.isArray(root.series)
    ? root.series
    : Array.isArray(root.buckets)
      ? root.buckets
      : Array.isArray(root.items)
        ? root.items
        : Array.isArray(root.data)
          ? root.data
          : [];
  return {
    rows,
    field_guide: Array.isArray(root.field_guide) ? root.field_guide : [],
    help: root.help || null,
    totals: root.totals && typeof root.totals === 'object' ? root.totals : null,
  };
}

export { DATA_SCOPE_PA };
